import {useCallback, useEffect, useState} from 'react';

const BASE = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
const POLL_MS = 4000;

function authHeaders() {
    const token = localStorage.getItem('access_token');
    return token
        ? {'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}
        : {'Content-Type': 'application/json'};
}

async function apiFetch(path, opts = {}) {
    const resp = await fetch(`${BASE}${path}`, {headers: authHeaders(), ...opts});
    if (resp.status === 404) return null;
    if (!resp.ok) {
        let detail = `${opts.method || 'GET'} ${path} → ${resp.status}`;
        try {
            const body = await resp.json();
            if (body?.error) detail = body.error;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
    if (resp.status === 204) return null;
    return resp.json();
}

export async function fetchGapSimilarity(gapId) {
    if (!gapId) return null;
    return apiFetch(`/api/fintel-gaps/${encodeURIComponent(gapId)}/similarity`);
}

export async function recomputeGapSimilarity(gapId) {
    if (!gapId) return null;
    return apiFetch(`/api/fintel-gaps/${encodeURIComponent(gapId)}/similarity/recompute`, {
        method: 'POST',
        body: '{}',
    });
}

/**
 * Load Gap similarity payload; poll while job is queued/running.
 * Same shape / lifecycle as {@link useEeiSimilarity}.
 */
export function useGapSimilarity(gapId, enabled = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [recomputing, setRecomputing] = useState(false);

    const reload = useCallback(async () => {
        if (!gapId || !enabled) {
            setData(null);
            return null;
        }
        try {
            const next = await fetchGapSimilarity(gapId);
            setData(next);
            setError(null);
            return next;
        } catch (e) {
            setError(e?.message || 'Failed to load similarity');
            return null;
        }
    }, [gapId, enabled]);

    useEffect(() => {
        if (!gapId || !enabled) {
            setData(null);
            setLoading(false);
            setError(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        reload().finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [gapId, enabled, reload]);

    const running = Boolean(data?.job?.running);

    useEffect(() => {
        if (!gapId || !enabled || !running) return undefined;
        const timer = setInterval(() => {
            reload();
        }, POLL_MS);
        return () => clearInterval(timer);
    }, [gapId, enabled, running, reload]);

    const recompute = useCallback(async () => {
        if (!gapId) return;
        setRecomputing(true);
        try {
            await recomputeGapSimilarity(gapId);
            await reload();
        } catch (e) {
            setError(e?.message || 'Failed to recompute similarity');
        } finally {
            setRecomputing(false);
        }
    }, [gapId, reload]);

    return {data, loading, error, running, recomputing, reload, recompute};
}
